/* composite widgets */
import React, { useState, useEffect, useRef } from "react";
import { Icon } from "./Icon.jsx";
import { Select, TextField, CurrencyField } from "./fields.jsx";
import { liveEstimate, fmt0, PERIODS, PRODUCTS, calcPayment, EXPENSE_GROUPS } from "../data/data.js";

/* ============================================================
   Repayment estimate hero  (variant: "full" | "bar" | "rail")
   ============================================================ */
export function EstimateHero({ f, variant = "full" }) {
  const e = liveEstimate(f);
  const freqWord = { weekly: "week", fortnightly: "fortnight", monthly: "month" }[e.freq];
  const has = e.principal > 0;
  const num = has ? fmt0(e.amount) : "-";

  if (variant === "bar") {
    return (
      <div className="pf-estimate" style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 18px" }}>
        <div className="pf-estimate__glow" />
        <div style={{ position: "relative", flex: 1 }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: "rgba(255,255,255,.6)" }}>Est. repayment</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginTop: 2 }}>
            <span className="pf-estimate__num" style={{ fontSize: 28 }}>{num}</span>
            <span style={{ fontSize: 14, color: "rgba(255,255,255,.7)" }}>/{freqWord}</span>
          </div>
        </div>
        <div style={{ position: "relative", textAlign: "right", fontSize: 12, color: "rgba(255,255,255,.6)", lineHeight: 1.4 }}>
          {fmt0(e.principal)}<br />over {e.term} yrs
        </div>
      </div>
    );
  }

  const big = variant === "rail" ? 46 : 56;
  return (
    <div className="pf-estimate" style={{ padding: variant === "rail" ? "26px 24px" : "30px 28px" }}>
      <div className="pf-estimate__glow" />
      <div style={{ position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <Icon name="trend" size={17} style={{ color: "var(--coral)" }} />
          <span style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: "rgba(255,255,255,.65)" }}>Estimated repayment</span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span className="pf-estimate__num" style={{ fontSize: big }}>{num}</span>
          <span style={{ fontSize: 19, color: "rgba(255,255,255,.7)", fontWeight: 500 }}>/{freqWord}</span>
        </div>
        <div style={{ height: 1, background: "rgba(255,255,255,.12)", margin: "20px 0 16px" }} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Stat label="Loan amount" val={has ? fmt0(e.principal) : "-"} />
          <Stat label="Term" val={`${e.term} years`} />
          <Stat label="Rate (indicative)" val={`${(e.rate * 100).toFixed(2)}% p.a.`} />
          <Stat label="Total repayable" val={has ? fmt0(e.amount * e.term * PERIODS[e.freq]) : "-"} />
        </div>
        <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.45)", marginTop: 18, lineHeight: 1.5 }}>
          Indicative only - actual rate &amp; repayments are subject to assessment.
        </div>
      </div>
    </div>
  );
}
function Stat({ label, val }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.55)", marginBottom: 3 }}>{label}</div>
      <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, color: "#fff" }}>{val}</div>
    </div>
  );
}

/* ============================================================
   Product cards (Step 5)
   ============================================================ */
export function ProductCards({ f, set }) {
  const e = liveEstimate(f);
  const freqWord = { weekly: "wk", fortnightly: "fn", monthly: "mo" }[f.paymentFrequency];
  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="pf-label" style={{ marginBottom: 10 }}>Repayment frequency</div>
        <div className="pf-tilegrid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {[["weekly", "Weekly"], ["fortnightly", "Fortnightly"], ["monthly", "Monthly"]].map(([v, l]) => (
            <button key={v} type="button" className={"pf-tile pf-tile--center" + (f.paymentFrequency === v ? " is-on" : "")}
              style={{ minHeight: 52, padding: "14px 8px" }} onClick={() => set({ paymentFrequency: v })}>
              <span className="pf-tile__title" style={{ fontSize: 15 }}>{l}</span>
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {PRODUCTS.map((p) => {
          const on = f.selectedProduct === p.id;
          const pay = calcPayment(e.principal, e.term, p.rate, f.paymentFrequency);
          return (
            <button key={p.id} type="button" className={"pf-tile" + (on ? " is-on" : "")} style={{ alignItems: "stretch", flexDirection: "column", gap: 0, padding: 0, overflow: "hidden" }} onClick={() => set({ selectedProduct: p.id })}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 18px" }}>
                <span className="pf-tile__check" style={{ marginTop: 0 }}>{on && <Icon name="check" size={13} />}</span>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="pf-tile__title" style={{ fontSize: 17 }}>{p.name}</span>
                    {p.recommended && <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: "#fff", background: "var(--coral)", borderRadius: 99, padding: "3px 8px" }}>Best for you</span>}
                  </div>
                  <div className="pf-tile__sub" style={{ marginTop: 3 }}>{p.tagline}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, letterSpacing: "-.02em" }}>{fmt0(pay)}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-45)" }}>/{freqWord}</div>
                </div>
              </div>
              <div style={{ borderTop: "1px solid var(--line)", background: on ? "rgba(255,255,255,.5)" : "var(--field)", padding: "11px 18px", display: "flex", gap: 16, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12.5, color: "var(--ink-70)", fontWeight: 600, whiteSpace: "nowrap" }}>{(p.rate * 100).toFixed(2)}% p.a.</span>
                <span style={{ fontSize: 12.5, color: "var(--ink-45)", whiteSpace: "nowrap" }}>{(p.comparison * 100).toFixed(2)}% comparison</span>
              </div>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--ink-45)", marginTop: 14, lineHeight: 1.55 }}>
        Comparison rate based on a {fmt0(30000)} loan over 5 years. WARNING: this comparison rate is true only for the examples given and may not include all fees and charges. Different terms, fees or other loan amounts might result in a different comparison rate.
      </div>
    </div>
  );
}

/* ============================================================
   Expense grid (Step 7)
   ============================================================ */
export function ExpenseGrid({ f, set }) {
  const exp = f.expenses || {};
  const total = Object.values(exp).reduce((a, b) => a + (Number(b) || 0), 0);
  const setExp = (k, v) => set({ expenses: { ...exp, [k]: v } });
  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {EXPENSE_GROUPS.map((g) => (
          <div key={g.id}>
            <div className="pf-group-head" style={{ marginBottom: 12 }}>{g.label}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {g.items.map((it) => {
                const max = it.max || 3000;
                const val = Number(exp[it.k] || 0);
                const pct = Math.min(100, (val / max) * 100);
                return (
                  <div key={it.k}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 7 }}>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>{it.l}</span>
                      <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15 }}>{fmt0(val)}<span style={{ fontSize: 12, color: "var(--ink-45)", fontWeight: 500 }}> /mo</span></span>
                    </div>
                    <input className="pf-slider" type="range" min={0} max={max} step={10} value={Math.min(val, max)}
                      onChange={(ev) => setExp(it.k, Number(ev.target.value))}
                      style={{ height: 6, background: `linear-gradient(90deg, var(--coral) ${pct}%, var(--field-2) ${pct}%)` }} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div style={{ position: "sticky", bottom: 0, marginTop: 20, padding: "14px 18px", background: "var(--ink)", borderRadius: 16, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 13.5, color: "rgba(255,255,255,.7)" }}>Total monthly expenses</span>
        <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22 }}>{fmt0(total)}</span>
      </div>
    </div>
  );
}

/* ============================================================
   Dynamic list (Step 8 — assets / liabilities)
   ============================================================ */
export function DynamicList({ kind, items, onChange, options, none, onNone }) {
  const isLia = kind === "liability";
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({});
  const add = () => {
    if (!draft.type) return;
    onChange([...items, { id: Date.now(), ...draft }]);
    setDraft({}); setAdding(false);
  };
  const remove = (id) => onChange(items.filter((x) => x.id !== id));
  const typeLabel = (v) => (options.find((o) => o.v === v) || {}).l || v;
  const typeIcon = (v) => (options.find((o) => o.v === v) || {}).icon || "doc";
  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {items.map((it) => (
          <div key={it.id} className="pf-card" style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
            <span className="pf-tile__icon" style={{ width: 38, height: 38 }}><Icon name={typeIcon(it.type)} size={19} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14.5 }}>{typeLabel(it.type)}{it.lender ? ` · ${it.lender}` : it.desc ? ` · ${it.desc}` : ""}</div>
              <div style={{ fontSize: 12.5, color: "var(--ink-45)" }}>
                {isLia ? `${fmt0(it.balance || 0)} owing · ${fmt0(it.monthly || 0)}/mo` : fmt0(it.value || 0)}
              </div>
            </div>
            <button type="button" className="pf-back" style={{ width: 36, height: 36 }} onClick={() => remove(it.id)}><Icon name="close" size={18} /></button>
          </div>
        ))}
      </div>

      {adding ? (
        <div className="pf-card pf-rise" style={{ padding: 16, marginTop: 10 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Select value={draft.type} onChange={(v) => setDraft({ ...draft, type: v })} options={options} placeholder={isLia ? "Type of debt" : "Type of asset"} />
            <TextField value={draft[isLia ? "lender" : "desc"]} onChange={(v) => setDraft({ ...draft, [isLia ? "lender" : "desc"]: v })} placeholder={isLia ? "Lender (e.g. Westpac)" : "Description (optional)"} />
            {isLia ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <CurrencyField value={draft.balance} onChange={(v) => setDraft({ ...draft, balance: v })} placeholder="Total owing" />
                <CurrencyField value={draft.monthly} onChange={(v) => setDraft({ ...draft, monthly: v })} placeholder="Repayment / month" />
              </div>
            ) : (
              <CurrencyField value={draft.value} onChange={(v) => setDraft({ ...draft, value: v })} placeholder="Estimated value" />
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="pf-btn pf-btn--ghost" style={{ flex: 1 }} onClick={() => { setAdding(false); setDraft({}); }}>Cancel</button>
              <button type="button" className="pf-btn" style={{ flex: 1, height: 48 }} disabled={!draft.type} onClick={add}>Add</button>
            </div>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => { setAdding(true); if (none) onNone(false); }}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", marginTop: 10, padding: "14px", borderRadius: 14, border: "1.5px dashed var(--line-2)", color: "var(--coral)", fontWeight: 600, fontSize: 15 }}>
          <Icon name="plus" size={18} /> Add {isLia ? "a liability" : "an asset"}
        </button>
      )}

      {items.length === 0 && (
        <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, fontSize: 14, color: "var(--ink-70)", cursor: "pointer" }}>
          <input type="checkbox" checked={none} onChange={(e) => onNone(e.target.checked)} style={{ width: 20, height: 20, accentColor: "var(--coral)" }} />
          {isLia ? "I have no other debts or liabilities" : "I have no assets to declare"}
        </label>
      )}
    </div>
  );
}

/* ============================================================
   Illion bank statement panel (Step 6)

   Real mode: the step shows a LAUNCHER; the button opens a FULL-PAGE overlay
   (back top-left, Continue bottom) with the illion iframe. Clicking the button
   runs `initiate` first if needed (creates the lender application and returns
   {base}/{prefix}{reference}, prefix from brand.config.js referencePrefix / BANK_STATEMENTS_SLUG_PREFIX).

   Completion: the parent page cannot observe network calls inside the
   cross-origin iframe (illion's POST …/v1/finished is invisible to us by
   browser design), so Continue unlocks on the completion SIGNAL instead:
     1. a postMessage from the illion origin matching complete/success/finish
     2. the bridge page — configure illion's completion redirect to
        {APP_BASE_URL}/illion-complete; the iframe lands on our own origin,
        which posts { type: "pf-illion-complete", reference } to the parent.
   Both fire at the same moment as v1/finished. (Note: the bridge message is
   trusted from OUR origin — for cross-origin embeds of the widget, rely on
   signal 1 or pass an absolute bridge origin via a future option.)

   Simulated mode (initiate absent, or the server has no lender credentials /
   bank-statements URL configured): the original mock connect flow runs so
   the form remains fully demoable.
   ============================================================ */
export function IllionPanel({ f, set, onComplete, initiate, status, relay }) {
  const done = !!f.illionReference;
  const [mockMode, setMockMode] = useState(!initiate);
  const [mockLoading, setMockLoading] = useState(false);
  const [open, setOpen] = useState(false);
  // overlay phase: loading | iframe | error
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState(null);
  // manual "I've finished" fallback (illion shows "you may now close this
  // window" inside the iframe; if no signal reaches us, the applicant confirms)
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { if (!open) setConfirming(false); }, [open]);

  const finish = (ref, how) => set({
    illionReference: ref || f.lenderReference || ("ILL-" + Date.now().toString(36).toUpperCase()),
    illionConfirmedBy: how || "signal",
  });

  /* completion signals — active while the overlay is open.

     illion's iframe reports progress to this PARENT page as JavaScript
     postMessages (docs.bankfeeds.com.au "JavaScript post messages"):
       { source: "BankStatements-SingleCustomerView", event, status,
         error_code?, error_message? }
     with events bank_selected → login_clicked → accounts_selected →
     submission_complete (statements retrieved for a bank) → submit_all
     (applicant finished with all banks). These NEVER arrive server-side —
     only the statements data itself does — so every one is relayed to our
     backend here for the ops receipt book. Requires illion to whitelist the
     hosting domain for postMessages.

     Completion is judged on the EVENT NAME, never on `status`: every
     progress event carries status "success", so matching status would mark
     the step complete the moment a bank is merely selected. */
  useEffect(() => {
    if (!open) return;
    let expectedOrigin = null;
    try { expectedOrigin = new URL(f.bankStatementsUrl).origin; } catch (e) { /* keep null */ }
    // Cap relays per overlay rather than dedupe: a multi-bank session fires
    // the same event names again for the next bank, and those repeats are
    // exactly what ops want to see. ~5 events per bank makes 24 generous.
    let relaysLeft = 24;
    const sendUp = (origin, d) => {
      if (!relay || relaysLeft <= 0) return;
      try {
        const event = d && typeof d === "object" ? d : { value: String(d).slice(0, 200) };
        relaysLeft -= 1;
        relay({ origin, ...event });
      } catch (e) { /* telemetry must never break the applicant */ }
    };
    const onMessage = (ev) => {
      const d = ev.data;
      // 2) our completion-redirect bridge (/illion-complete) — same origin as the form
      if (ev.origin === window.location.origin && d && typeof d === "object" && d.type === "pf-illion-complete") {
        sendUp(ev.origin, d);
        if (!done) finish(typeof d.reference === "string" ? d.reference : null, "signal");
        return;
      }
      // 1) exact iframe origin, or a *.bankstatements.com.au host (label-anchored
      // so lookalike domains like evilbankstatements.com.au don't match)
      const host = (() => { try { return new URL(ev.origin).hostname; } catch (e) { return ""; } })();
      const fromIllion = (expectedOrigin && ev.origin === expectedOrigin) || /(^|\.)bankstatements\.com\.au$/.test(host);
      if (!fromIllion) return;
      sendUp(ev.origin, d);
      if (done) return;
      // The event/type NAME decides completion (submission_complete carries
      // "complete"; legacy shapes say complete/finish/success outright).
      const name = d && typeof d === "object"
        ? (typeof d.event === "string" && d.event) || (typeof d.type === "string" && d.type) || ""
        : (typeof d === "string" ? d : "");
      if (/submission_complete|submit_all|complete|finish|success/i.test(name)) {
        finish((d && typeof d === "object" && (d.reference || d.ref)) || null, "signal");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [open, done, f.bankStatementsUrl, f.lenderReference]); // eslint-disable-line

  /* 3) webhook completion — illion POSTs the statements to our server the
     moment the applicant finishes, so while the overlay is open we poll the
     session status (works even when no redirect signal ever reaches the
     browser). Marking done tears this effect down; the auto-close below
     picks it up from there. */
  useEffect(() => {
    if (!open || done || !status) return;
    let stopped = false;
    const check = async () => {
      try {
        const r = await status();
        if (!stopped && r && r.completed) finish(null, "webhook");
      } catch (e) { /* polling stays silent */ }
    };
    check();
    const t = setInterval(check, 4000);
    return () => { stopped = true; clearInterval(t); };
  }, [open, done]); // eslint-disable-line

  /* Webhook-confirmed completion closes the overlay itself after a beat —
     the applicant sees "statements received" and moves on without having to
     find Continue. (Signal/manual completions keep the existing behaviour.) */
  useEffect(() => {
    if (!open || !done || f.illionConfirmedBy !== "webhook") return;
    const t = setTimeout(() => { setOpen(false); if (onComplete) onComplete(); }, 1200);
    return () => clearTimeout(t);
  }, [open, done]); // eslint-disable-line

  /* lock page scroll behind the full-page overlay */
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const openOverlay = async () => {
    setOpen(true);
    setError(null);
    if (f.bankStatementsUrl) { setPhase("iframe"); return; }
    setPhase("loading");
    try {
      const r = await initiate();
      if (r && r.bankStatementsUrl && !r.simulated) setPhase("iframe");
      else { setOpen(false); setMockMode(true); } // server is in simulated mode
    } catch (e) {
      setError((e && e.message) || "We couldn't start the secure bank connection.");
      setPhase("error");
    }
  };
  const retry = () => { setError(null); openOverlay(); };
  const handleContinue = () => { setOpen(false); if (onComplete) onComplete(); };

  const mockConnect = () => {
    setMockLoading(true);
    setTimeout(() => {
      setMockLoading(false);
      set({ illionReference: "ILL-" + Math.random().toString(36).slice(2, 8).toUpperCase(), illionConfirmedBy: "mock" });
      if (onComplete) setTimeout(onComplete, 1400);
    }, 2600);
  };

  const hostText = (() => {
    if (f.bankStatementsUrl) { try { return new URL(f.bankStatementsUrl).hostname; } catch (e) { /* fall through */ } }
    return "bankstatements.com.au";
  })();

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
        {[["lock", "Bank-grade 256-bit encryption"], ["shield", "Read-only - we can never move your money"], ["spark", "Takes about 60 seconds"]].map(([ic, tx]) => (
          <div key={tx} className="pf-trust" style={{ fontSize: 14, color: "var(--ink-70)" }}>
            <span style={{ width: 32, height: 32, borderRadius: 9, background: "var(--success-tint)", color: "var(--success)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon name={ic} size={17} /></span>
            {tx}
          </div>
        ))}
      </div>

      <div style={{ borderRadius: 18, overflow: "hidden", border: "1px solid var(--line)", background: "#fff", boxShadow: "var(--sh-1)" }}>
        <div style={{ padding: "32px 24px", textAlign: "center", minHeight: 200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          {done ? (
            <React.Fragment>
              <span className="pf-pop" style={{ width: 60, height: 60, borderRadius: "50%", background: "var(--success)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, animation: "pf-pop .5s both" }}><Icon name="check" size={32} /></span>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 17 }}>Statements received</div>
              <div style={{ fontSize: 13, color: "var(--ink-45)", marginTop: 6 }}>Reference {f.illionReference}</div>
            </React.Fragment>
          ) : mockLoading ? (
            <React.Fragment>
              <span className="pf-spin" style={{ width: 44, height: 44, border: "4px solid var(--field-2)", borderTopColor: "var(--coral)", borderRadius: "50%", marginBottom: 18 }} />
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16 }}>Securely retrieving statements…</div>
              <div style={{ fontSize: 13, color: "var(--ink-45)", marginTop: 6 }}>This won't take long.</div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <span style={{ width: 56, height: 56, borderRadius: 16, background: "var(--coral-tint)", color: "var(--coral)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}><Icon name="bank" size={28} /></span>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 17, marginBottom: 6 }}>Connect your bank</div>
              <div style={{ fontSize: 13.5, color: "var(--ink-45)", maxWidth: 300, marginBottom: 20 }}>
                {mockMode ? "Choose your bank and log in securely. Powered by illion BankStatements." : "Opens full screen - choose your bank and log in securely, then press Continue when you're done."}
              </div>
              <button type="button" className="pf-btn" onClick={mockMode ? mockConnect : openOverlay}>
                {mockMode ? "Connect securely" : "Open bank statements"}
              </button>
            </React.Fragment>
          )}
        </div>
      </div>
      <div style={{ textAlign: "center", marginTop: 14, fontSize: 12, color: "var(--ink-25)", fontWeight: 600, letterSpacing: ".04em" }}>POWERED BY illion BankStatements</div>

      {open && (
        <div className="pf-illion-overlay" role="dialog" aria-modal="true" aria-label="Bank statement verification">
          <div className="pf-illion-top">
            <button type="button" className="pf-back" onClick={() => setOpen(false)} aria-label="Back" style={{ border: "1.5px solid var(--line-2)", flexShrink: 0 }}>
              <Icon name="arrowLeft" size={20} />
            </button>
            <div className="pf-illion-url"><Icon name="lock" size={13} /><span>{hostText}</span></div>
            <div style={{ width: 40, flexShrink: 0 }} />
          </div>

          <div className="pf-illion-body">
            {phase === "iframe" && f.bankStatementsUrl ? (
              <iframe
                src={f.bankStatementsUrl}
                title="Bank statement verification - illion BankStatements"
                allow="camera *"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
              />
            ) : (
              <div className="pf-illion-center">
                {phase === "error" ? (
                  <React.Fragment>
                    <span style={{ width: 56, height: 56, borderRadius: 16, background: "var(--coral-tint)", color: "var(--coral)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}><Icon name="info" size={28} /></span>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 17, marginBottom: 6 }}>Something went wrong</div>
                    <div style={{ fontSize: 13.5, color: "var(--ink-45)", maxWidth: 300, marginBottom: 20 }}>{error || "We couldn't start the secure bank connection. Please try again."}</div>
                    <button type="button" className="pf-btn" onClick={retry}>Try again</button>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <span className="pf-spin" style={{ width: 44, height: 44, border: "4px solid var(--field-2)", borderTopColor: "var(--coral)", borderRadius: "50%", marginBottom: 18 }} />
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16 }}>Setting up your secure session…</div>
                    <div style={{ fontSize: 13, color: "var(--ink-45)", marginTop: 6 }}>Registering your application. This won't take long.</div>
                  </React.Fragment>
                )}
              </div>
            )}
          </div>

          <div className="pf-illion-foot">
            <div className={"pf-illion-hint" + (done ? " is-done" : "")}>
              {done ? (
                <React.Fragment><Icon name="check" size={15} style={{ verticalAlign: -2, marginRight: 5 }} />All done - your statements were received securely.</React.Fragment>
              ) : confirming ? (
                <span className="pf-illion-confirm">
                  Did illion confirm your statements were submitted?
                  <button type="button" className="pf-illion-confirm__yes" onClick={() => { setConfirming(false); finish(null, "manual"); }}>Yes - I'm done</button>
                  <button type="button" className="pf-illion-confirm__no" onClick={() => setConfirming(false)}>Not yet</button>
                </span>
              ) : (
                <span>
                  Finish the steps above - Continue unlocks automatically when your bank confirms.
                  <button type="button" className="pf-illion-manual" onClick={() => setConfirming(true)}>
                    illion said I can close this window →
                  </button>
                </span>
              )}
            </div>
            <button type="button" className="pf-btn" disabled={!done} onClick={handleContinue}>
              Continue <Icon name="arrowRight" size={19} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Supplied bank statements (asset finance)
   ============================================================
   Brokers writing asset finance usually already hold the illion statements -
   they ran them before the deal reached us. Sending the client back through
   the connect flow to produce a second copy wastes everyone's time, so this
   panel takes what the broker already has: the PDF, the illion document ID,
   or both.

   It sits BESIDE the connect flow, never replacing it: a direct applicant
   still connects their bank, and the panel above stays the primary path.

   What it deliberately does NOT do is pretend a supplied file is an illion
   delivery. The document is stored and labelled as supplied; the statements
   report (which renders illion's own JSON feed) is untouched and stays empty
   unless a real delivery arrives. Ops can see exactly which they have.
   ============================================================ */
export function SuppliedStatementsPanel({ f, set, supply }) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const inputRef = useRef(null);

  const onFile = async (file) => {
    if (!file || !supply) return;
    setProblem(null);
    // 20MB is the ceiling on the direct-to-storage path; anything larger is
    // not a statements PDF and would fail server-side anyway.
    if (file.size > 20 * 1024 * 1024) { setProblem("That file is over 20MB - please send the statements PDF only."); return; }
    setBusy(true);
    const res = await supply({ file });
    setBusy(false);
    if (res && res.ok) set({ statementsSupplied: true, statementsFileName: file.name });
    else setProblem(res && res.error === "unsupported_type"
      ? "That file type isn't supported - a PDF is ideal."
      : "We couldn't upload that just now. You can try again, or continue and send it to us later.");
  };

  return (
    <div className="pf-card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="pf-tile__icon" style={{ width: 34, height: 34 }}><Icon name="doc" size={17} /></span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14.5 }}>Already have the statements?</div>
          <div style={{ fontSize: 12.5, color: "var(--ink-45)" }}>Brokers: if you've already run illion for this client, hand them over here instead.</div>
        </div>
      </div>

      {f.statementsSupplied === true ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", borderRadius: 12, background: "var(--success-tint)" }}>
          <Icon name="check" size={17} style={{ color: "var(--success)", flexShrink: 0 }} />
          <span style={{ fontSize: 13.5, color: "var(--ink-70)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {f.statementsFileName || "Statements"} uploaded
          </span>
          <button type="button" className="pf-btn pf-btn--text" onClick={() => { set({ statementsSupplied: false, statementsFileName: "" }); }}>
            Replace
          </button>
        </div>
      ) : (
        <div>
          <input ref={inputRef} type="file" accept="application/pdf,.pdf" style={{ display: "none" }}
            onChange={(e) => { const file = e.target.files && e.target.files[0]; e.target.value = ""; onFile(file); }} />
          <button type="button" className="pf-btn pf-btn--ghost" disabled={busy || !supply} onClick={() => inputRef.current && inputRef.current.click()}>
            {busy ? "Uploading…" : "Upload the statements PDF"}
          </button>
        </div>
      )}

      <div>
        <div className="pf-label">illion document ID (optional)</div>
        <div style={{ marginTop: 6 }}>
          <TextField value={f.illionDocumentId} onChange={(v) => set({ illionDocumentId: v })} placeholder="e.g. the report or reference id from illion" />
        </div>
        <div style={{ fontSize: 12, color: "var(--ink-45)", marginTop: 6 }}>
          If you have the illion reference, adding it lets us match your upload to their record.
        </div>
      </div>

      {problem && (
        <div style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "11px 13px", borderRadius: 12, background: "var(--coral-tint-2)", border: "1px solid var(--coral)" }}>
          <Icon name="info" size={17} style={{ color: "var(--coral)", flexShrink: 0, marginTop: 1 }} />
          <span style={{ fontSize: 13, color: "var(--ink-70)", lineHeight: 1.5 }}>{problem}</span>
        </div>
      )}
    </div>
  );
}
